import { useEffect } from 'react';
import useSWR from 'swr';
import { subscribeConfigModulosChange } from 'app/react-bridge/config-modulos-change';

import axiosInstance from '../../../http/axios-instance';
import { swrConfig } from '../../../http/swr-config';
import { ConfigConta, ConfigModulos, ConfigPsim, TipoDocumentoAdmin } from '../core/types';
import {
  saveConfigContaStorage,
  saveConfigModulosStorage,
  saveConfigPsimStorage,
  saveMulticontaStorage,
} from './session-storage';

const LEI_DADOS_TIPO = 'LEI_DADOS';

// Timeouts por chamada (Requirements 7.1, 9.4, 15.1): cada fonte tem um limite
// próprio; estourar o axios rejeita a promise, e o SWR resolve para o fallback
// seguro do hook (nunca propaga o erro para o menu).
const CONFIG_MODULOS_TIMEOUT_MS = 30_000;
const CONFIG_PSIM_TIMEOUT_MS = 30_000;
const CONFIGURACOES_TIMEOUT_MS = 30_000;
const MULTICONTA_TIMEOUT_MS = 10_000;
const PRE_CADASTROS_TIMEOUT_MS = 5_000;

const CONFIG_MODULOS_URL = 'config_modulos/1';
const CONFIG_PSIM_URL = 'config_psim/1';

/**
 * GET config_modulos/1; só busca quando `enabled` (tenant + permissão de leitura
 * de configurações). Persiste no storage ao obter sucesso, para a folha Angular
 * ler o que o React gravou; falha/timeout/disabled resolve para `null` (módulos
 * não habilitados).
 *
 * A key inclui `tenantKey`: o config é por-tenant, mas a URL não carrega o tenant
 * (vai no header). Sem isso, com o `revalidateIfStale: false` do swrConfig, trocar
 * de conta serviria o config em cache da conta anterior — o menu (CFTV/VMS) só
 * corrigia no F5, que zera o cache em memória. Escopar a key pelo tenant faz a
 * troca de conta virar uma nova key, e o SWR rebusca para o tenant novo.
 *
 * Salvar os módulos em Integrações (folha Angular) não muda a key; sem revalidar
 * no aviso da folha, o menu só refletiria os módulos ativados após o F5.
 */
export function useConfigModulos(
  enabled: boolean,
  tenantKey: string | null,
): {
  configModulos: ConfigModulos | null;
  isLoading: boolean;
} {
  const { data, isLoading, mutate } = useSWR<ConfigModulos>(
    enabled && tenantKey ? [CONFIG_MODULOS_URL, tenantKey] : null,
    ([url]: [string, string]) =>
      axiosInstance
        .get<ConfigModulos>(url, { timeout: CONFIG_MODULOS_TIMEOUT_MS })
        .then((res) => res.data),
    {
      ...swrConfig,
      onSuccess: (config) => saveConfigModulosStorage(config),
    },
  );

  // Com a key desabilitada (null), o mutate do SWR é no-op: não há fetch fora do gate.
  useEffect(() => {
    return subscribeConfigModulosChange(() => {
      void mutate();
    });
  }, [mutate]);

  return { configModulos: data ?? null, isLoading };
}

/**
 * GET config_psim/1; só busca quando `enabled` (tenant). Sem gate de permissão:
 * o endpoint não exige `CONFIGURACOES_READ` — telas do morador comum (portaria,
 * previsão de visita, etc.) chamam este mesmo GET diretamente. Persiste no
 * storage ao obter sucesso (mesmas 3 chaves que os pontos de escrita Angular
 * gravam), para a folha ler o que o React grava; falha/timeout/disabled resolve
 * para `null` (PSIM tratado como não habilitado).
 *
 * A key inclui `tenantKey`, mesmo motivo de `useConfigModulos`: o config é
 * por-tenant mas a URL não carrega o tenant (vai no header). Sem isso, com o
 * `revalidateIfStale: false` do swrConfig, trocar de morador serviria a config
 * em cache do morador anterior — o Timeline só corrigia no F5.
 */
export function useConfigPsim(
  enabled: boolean,
  tenantKey: string | null,
): { configPsim: ConfigPsim | null; isLoading: boolean } {
  const { data, isLoading } = useSWR<ConfigPsim>(
    enabled && tenantKey ? [CONFIG_PSIM_URL, tenantKey] : null,
    ([url]: [string, string]) =>
      axiosInstance
        .get<ConfigPsim>(url, { timeout: CONFIG_PSIM_TIMEOUT_MS })
        .then((res) => res.data),
    {
      ...swrConfig,
      onSuccess: (config) => saveConfigPsimStorage(config),
    },
  );

  return { configPsim: data ?? null, isLoading };
}

/**
 * GET public/configuracoes/1; só busca quando `enabled` (tenant ou permissão de
 * leitura de configurações). Persiste no storage ao obter sucesso; falha/timeout/
 * disabled resolve para `null` (rótulos padrão de divisão/unidade).
 */
export function useConfiguracoes(enabled: boolean): {
  config: ConfigConta | null;
  isLoading: boolean;
} {
  const { data, isLoading } = useSWR<ConfigConta>(
    enabled ? 'public/configuracoes/1' : null,
    (key: string) =>
      axiosInstance
        .get<ConfigConta>(key, { timeout: CONFIGURACOES_TIMEOUT_MS })
        .then((res) => res.data),
    {
      ...swrConfig,
      onSuccess: (config) => saveConfigContaStorage(config),
    },
  );

  return { config: data ?? null, isLoading };
}

/**
 * GET contas/multiconta; só busca quando `enabled` (storage 'multiconta'
 * ausente). Persiste no storage ao obter sucesso; falha/timeout resolve para
 * `false` (não multiconta), nunca `null` — não há estado "desconhecido" para o
 * chamador tratar.
 */
export function useMulticonta(enabled: boolean): { ehMulticonta: boolean; isLoading: boolean } {
  const { data, isLoading } = useSWR<boolean>(
    enabled ? 'contas/multiconta' : null,
    (key: string) =>
      axiosInstance.get<boolean>(key, { timeout: MULTICONTA_TIMEOUT_MS }).then((res) => res.data),
    {
      ...swrConfig,
      onSuccess: (value) => saveMulticontaStorage(value),
    },
  );

  return { ehMulticonta: data ?? false, isLoading };
}

/**
 * GET admin/pre_cadastro/exists_aguardando; só busca quando `enabled`
 * (Usuario_Logado é Integrador). Sem persistência no storage — o marcador de
 * atenção não é espelhado pelo Angular. Falha/timeout/disabled → `false`.
 */
export function usePreCadastrosAguardando(enabled: boolean): { contemPreCadastros: boolean } {
  const { data } = useSWR<boolean>(
    enabled ? 'admin/pre_cadastro/exists_aguardando' : null,
    (key: string) =>
      axiosInstance
        .get<boolean>(key, { timeout: PRE_CADASTROS_TIMEOUT_MS })
        .then((res) => res.data),
    swrConfig,
  );

  return { contemPreCadastros: data ?? false };
}

/**
 * GET admin/tipo_documento/by_tenant/; sempre habilitado (o rótulo de LGPD não
 * depende de tenant/permissão). Sem timeout dedicado: a AC não define um limite
 * explícito para esta fonte, e o `swrConfig` sem retry evita reter o menu numa
 * chamada lenta. Ausência do doc 'LEI_DADOS' ou falha → `null` (o item cai no
 * título traduzido via react-i18next).
 */
export function useDocumentoLeiDados(): { lgpdLabel: string | null } {
  const { data } = useSWR<TipoDocumentoAdmin[]>('admin/tipo_documento/by_tenant/', swrConfig);

  const leiDados = data?.find((doc) => doc.tipo === LEI_DADOS_TIPO);
  return { lgpdLabel: leiDados?.descricao ?? null };
}
